import React, { useState, useEffect, useMemo } from 'react';
import { Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Tooltip } from 'chart.js';
import { useDarkMode } from '../../../DarkModeContext';
import Loading from '../../../components/loading';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip);

const lowImpactAreaPlugin = {
  id: 'lowImpactArea',
  beforeDraw: (chart) => {
    const ctx = chart.ctx;
    const xAxis = chart.scales.x;
    const yAxis = chart.scales.y;
    const origin = xAxis.getPixelForValue(0);    
    const yorigin = yAxis.getPixelForValue(0);
    const chartRight = chart.chartArea.right;
    const chartTop = chart.chartArea.top;
    const xYellowThreshold = xAxis.getPixelForValue(70);
    const yYellowThreshold = yAxis.getPixelForValue(4000);
    
    // Draw red background for the entire chart area
    ctx.save();
    ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
    ctx.fillRect(origin, chartTop, chartRight - origin, yorigin - chartTop);
    ctx.restore();
    
    // Draw yellow area on top
    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 0, 0.5)';
    ctx.fillRect(origin, yorigin, xYellowThreshold - origin, yYellowThreshold - yorigin);
    ctx.restore();
  }
};


const SelectedTeamAnalytics = () => {
  const [groupSessions, setGroupSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionType, setSessionType] = useState('allTime');
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const { teamId } = useParams();

  const fetchData = async (url) => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('Token from localStorage:', token);
  
      if (!token) {
        console.log('No token found, redirecting to sign-in');
        navigate('/auth/sign-in');
        return;
      }
      
      const headers = new Headers();
      headers.set('Authorization', `Token ${token}`);
      console.log('Headers:', headers);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include',
        signal: AbortSignal.timeout(60000)
      });
  
      console.log('Response status:', response.status);
  
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/auth/sign-in');
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 500) {
          throw new Error('Server error. The team is working on resolving this issue.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      console.log('Fetched data:', data);
  
      if (Array.isArray(data)) {
        setGroupSessions(data);
        if (data.length > 0) {
          setSelectedSession(data[0]);
        }
      } else {
        setSelectedSession(data);
      }
      setLoading(false);
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
      setError(error.message);
      setLoading(false);
      toast.error(`Failed to fetch session data: ${error.message}`);
    }
  };

  useEffect(() => {
    if (teamId) {
      setLoading(true);
      setError(null);
      console.log('Session type changed to:', sessionType);
      if (sessionType === 'allTime' || sessionType === 'custom') {
        fetchData(`https://staging-sit.com/api/team-sessions/${teamId}/group_sessions/`);
      } else if (sessionType === 'lastSession') {
        fetchData(`https://staging-sit.com/api/team-sessions/${teamId}/latest_group_session/`);
      }
    }
  }, [teamId, sessionType]);

  const handleGoBack = () => {
    navigate('/admin/team-analytics');
  };

  const handleSessionTypeChange = (type) => {
    console.log('Changing session type to:', type);
    setSessionType(type);
  };

  const handleSessionChange = (event) => {
    const selectedSessionId = parseInt(event.target.value);
    const newSelectedSession = groupSessions.find(session => session.group_session_id === selectedSessionId);
    setSelectedSession(newSelectedSession);
  };

  const chartData = useMemo(() => {
    console.log('Preparing chart data. Selected session:', selectedSession);
    if (!selectedSession || !selectedSession.players) {
      return { datasets: [{ data: [] }] };
    }

    const allImpacts = selectedSession.players.flatMap(player => 
      (player.impacts || []).map(impact => ({
        x: impact.linear_impact,
        y: impact.rotational_impact,
        time: impact.time,
        player: player.player.username
      }))
    );

    console.log('Number of impacts:', allImpacts.length);

    return {
      datasets: [{
        label: 'Team Impacts',
        data: allImpacts,
        backgroundColor: 'rgba(90, 34, 139, 0.6)',
        borderColor: 'white',
        borderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      }],
    };
  }, [selectedSession]);

  const options = useMemo(() => {
    const xMax = Math.max(...chartData.datasets[0].data.map(d => d.x), 140);
    const yMax = Math.max(...chartData.datasets[0].data.map(d => d.y), 8000);

    return {
      scales: {
        x: {
          type: 'linear',
          position: 'bottom',
          title: {
            display: true,
            text: 'Linear Impact (g)',
            color: isDarkMode ? 'white' : 'black'
          },
          min: 0,
          max: xMax * 1.1, // Add 10% padding
          ticks: {
            color: isDarkMode ? 'white' : 'black'
          },
          grid: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        },
        y: {
          type: 'linear',
          position: 'left',
          title: {
            display: true,
            text: 'Rotational Impact (rad/s²)',
            color: isDarkMode ? 'white' : 'black'
          },
          min: 0,
          max: yMax * 1.1, // Add 10% padding
          ticks: {
            color: isDarkMode ? 'white' : 'black'
          },
          grid: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context) => {
              const point = context.raw;
              return [
                `Player: ${point.player}`,
                `Time: ${new Date(point.time).toLocaleString()}`,
                `Linear Acceleration: ${point.x.toFixed(2)} g`,
                `Rotational Acceleration: ${point.y.toFixed(2)} rad/s²`
              ];
            },
            title: () => null,
          },
          itemSort: (a, b) => b.raw.y - a.raw.y,
          filter: (tooltipItem, index, tooltipItems) => index === 0,
          backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
          titleColor: isDarkMode ? 'white' : 'black',
          bodyColor: isDarkMode ? 'white' : 'black',
        },
        legend: {
          display: false
        },
      },
      responsive: true,
      maintainAspectRatio: false
    };
  }, [chartData, isDarkMode]);

  if (loading) return <div className={isDarkMode ? "text-white" : ""}><Loading/></div>;
  if (error) return <div className={isDarkMode ? "text-white" : ""}>Error: {error}</div>;

  return (
    <div className={`w-full ${isDarkMode ? 'bg-[#252540]' : ''} p-4`}>
      <div className="flex justify-between items-center mb-4">
        <button 
          onClick={handleGoBack}
          className="bg-[#f46524] text-white px-4 py-2 rounded hover:bg-[#f46570] transition-colors duration-200"
        >
          GO BACK
        </button>
      </div>
      <div className="flex justify-between items-center mb-4">
        <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : ''}`}>
          Team Impact Analysis: {selectedSession?.team_name || `Team ${teamId}`}
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => handleSessionTypeChange('allTime')}
            className={`px-4 py-2 rounded ${sessionType === 'allTime' ? 'bg-[#f46524] text-white' : 'bg-gray-200 text-gray-800'}`}
          >
            All Time
          </button>
          <button
            onClick={() => handleSessionTypeChange('lastSession')}
            className={`px-4 py-2 rounded ${sessionType === 'lastSession' ? 'bg-[#f46524] text-white' : 'bg-gray-200 text-gray-800'}`}
          >
            Last Session
          </button>
          <button
            onClick={() => handleSessionTypeChange('custom')}
            className={`px-4 py-2 rounded ${sessionType === 'custom' ? 'bg-[#f46524] text-white' : 'bg-gray-200 text-gray-800'}`}
          >
            Custom
          </button>
        </div>
      </div>
      
      {sessionType === 'custom' && groupSessions.length > 0 && (
        <div className="mb-4">
          <select
            value={selectedSession?.group_session_id || ''}
            onChange={handleSessionChange}
            className="p-2 border rounded"
          >
            {groupSessions.map((session) => (
              <option key={session.group_session_id} value={session.group_session_id}>
                {new Date(session.start_time).toLocaleString()} - {session.name}
              </option>
            ))}
          </select>
        </div>
      )}
      
      <div className={`${isDarkMode ? 'bg-[#303050]' : 'bg-white'} p-4 rounded-lg shadow-lg`} style={{ height: '500px' }}>
        {chartData.datasets[0].data.length > 0 ? (
          <Scatter data={chartData} options={options} plugins={[lowImpactAreaPlugin]} />
        ) : (
          <div className={`text-center ${isDarkMode ? 'text-white' : ''}`}>No impact data available for this team.</div>
        )}
      </div>

      {selectedSession && (
        <div className={`mt-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
          <h3 className="text-lg font-semibold">Session Summary:</h3>
          <p>Session Name: {selectedSession.name}</p>
          <p>Start Time: {new Date(selectedSession.start_time).toLocaleString()}</p>
          <p>End Time: {selectedSession.end_time ? new Date(selectedSession.end_time).toLocaleString() : 'Ongoing'}</p>
          <p>Sport: {selectedSession.sport}</p>
          <p>Category: {selectedSession.category}</p>
          
        </div>
      )}
    </div>
  );
};

export default SelectedTeamAnalytics;